<template>
    <auth-layout
        :image="1"
        :byword="$t('pages.auth.signup.steps.register.byword')"
        :progress="progress"
        :isProgressBarHidden="true"
    >
        <common-content-wrapper width="small">
            <common-page-header :heading="$t('pages.auth.signup.steps.register.heading_two')">
                <template v-if="dataForPrefill.practiceName" #content>
                    {{ $t('pages.auth.signup.steps.register.description.invite') }}
                    <br />
                    <b>{{ dataForPrefill.practiceName }}</b>
                </template>
                <template v-else #content>
                    {{ $t('pages.auth.signup.steps.register.description.or') }}
                    <router-link :to="{ name: 'auth.login' }" :class="loginLinkClass">
                        {{ $t('pages.auth.signup.steps.register.description.login') }}
                    </router-link>
                </template>
            </common-page-header>
            <common-modal v-if="billingPricingModalOpen" width="pricing" @close="toggleBillingPricingModal">
                <pricing-cards :showButtons="false" :useFallback="true"></pricing-cards>
            </common-modal>
        </common-content-wrapper>

        <common-content-wrapper width="small">
            <register-form
                :organizationRoles="organizationRoles"
                :specialities="specialities"
                :physicianGroups="physicianGroups"
                :dataForPrefill="dataForPrefill"
                :isComingFromInvite="isComingFromInvite"
                :updateDemo="true"
                @sent="goToNextStep()"
                @form-submitted="isFormSubmitted($event)"
            ></register-form>
        </common-content-wrapper>
        <common-content-wrapper width="small">
            <div class="billing-info">
                {{ $t('pages.practice.billing.registration_billing_info') }}
                <a @click="toggleBillingPricingModal">{{ $t('pages.practice.billing.see_plans') }}</a>
            </div>
        </common-content-wrapper>
    </auth-layout>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import UserRolesAPI from 'api/UserRolesAPI'
    import SpecialitiesAPI from 'api/SpecialitiesAPI'
    import SignupPageMixin from 'mixins/SignupPageMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AuthLayout from 'components/layouts/AuthLayout'
    import RegisterForm from 'components/forms/AuthSignupRegisterForm'
    import UserRolesEnum from '@/enums/UserRolesEnum'
    import UserTypeEnum from '@/enums/UserTypeEnum'
    import PhysicianGroupsAPI from 'api/PhysicianGroupsAPI'
    import PricingCards from 'components/page-elements/practice/billing/PricingCards'

    export default {
        mixins: [SignupPageMixin, PageWithDataMixin],

        components: {
            AuthLayout,
            RegisterForm,
            PricingCards,
        },

        data() {
            return {
                organizationRoles: [],
                specialities: [],
                physicianGroups: [],
                dataForPrefill: {},
                isComingFromInvite: false,
                isDisabled: false,
                billingPricingModalOpen: false,
            }
        },

        computed: {
            loginLinkClass() {
                return {
                    'login-link__disabled': this.isDisabled,
                }
            },
        },

        loadData() {
            return {
                specialities: SpecialitiesAPI.getListMultiselect(),
                organizationRoles: UserRolesAPI.getList(),
                physicianGroups: PhysicianGroupsAPI.getList(),
            }
        },

        methods: {
            isFormSubmitted(event) {
                this.isDisabled = event.loginLinkDisabled ? true : false
            },

            toggleBillingPricingModal() {
                this.billingPricingModalOpen = !this.billingPricingModalOpen
            },
        },

        mounted() {
            this.physicianGroups = [{ label: 'None', value: -1, data: { displayName: 'None', id: -1 } }, ...this.physicianGroups]
            const joinInviteToken = Object.keys(this.$route.query)
                .filter((key) => /^.*jit$/.test(key))
                .map((key) => this.$route.query[key])[0]
            if (joinInviteToken && /^[0-9A-Fa-f]+$/.test(joinInviteToken)) {
                UsersAPI.getJoinInviteByToken(joinInviteToken, [
                    'id',
                    'toEmail',
                    'practiceType',
                    'practiceName',
                    'practice(id,name)',
                    'userRoleId',
                    'inviteType',
                    'fromUserRoleId',
                    'firstName',
                    'lastName',
                    'canActivateAnotherUser',
                ])
                    .then((res) => {
                        const dataForPrefill = {
                            joinInviteToken,
                            email: res.toEmail,
                            firstName: res.firstName,
                            lastName: res.lastName,
                            practiceName: res.practiceName,
                            userRoleId: res.userRoleId,
                        }
                        if (res.userRoleId === UserRolesEnum.NURSE_PRACTITIONER.ID) {
                            dataForPrefill.canActivateAnotherUser = !!res.canActivateAnotherUser
                        }
                        if (res.practice) {
                            dataForPrefill.practiceName = res.practice.name
                            dataForPrefill.practiceId = res.practice.id
                        }
                        if (res.practiceType) {
                            dataForPrefill.practiceType = res.practiceType
                        }
                        this.dataForPrefill = dataForPrefill
                        this.isComingFromInvite = true
                        if (
                            res.inviteType === UserTypeEnum.PRACTICE &&
                            res.practiceName &&
                            ![
                                UserRolesEnum.PHYSICIAN.ID,
                                UserRolesEnum.NURSE_PRACTITIONER.ID,
                                UserRolesEnum.OFFICE_MANAGER.ID,
                            ].includes(res.fromUserRoleId)
                        ) {
                            this.organizationRoles = this.organizationRoles.filter((orientationRole) => {
                                return [
                                    UserRolesEnum.PHYSICIAN.ID,
                                    UserRolesEnum.NURSE_PRACTITIONER.ID,
                                    UserRolesEnum.OFFICE_MANAGER.ID,
                                ].includes(orientationRole.data.id)
                            })
                        }
                    })
                    .catch(() => {})
            }
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = 'mobile'
        },

        beforeDestroy() {
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = ''
        },
    }
</script>

<style lang="scss" scoped>
    .login {
        &-link {
            &__disabled {
                pointer-events: none;
                opacity: 0.2;
            }
        }
    }
</style>
